import { useState } from 'react';

/**
 * Kiro — mascote do Kevins Clínicas (esquilo com óculos de aviador + cachecol).
 *
 * IMPORTANTE: o Kiro é arte ILUSTRADA (raster), não SVG desenhado à mão.
 * Este componente só CONSOME os arquivos de imagem exportados pelo
 * design/gerador. Os PNGs (fundo transparente, 2x) ficam em
 * `apps/web/public/kiro/<nome>.png` — ver `public/kiro/README.md` pro
 * contrato de nomes e specs.
 *
 * Fallback gracioso: se o asset ainda não existe (404), renderiza
 * `fallback` (ou nada) — então dá pra plugar o componente no app ANTES
 * de os PNGs chegarem, sem quebrar tela com ícone de imagem quebrada.
 */

export type KiroAsset =
  // Expressões (busto/rosto)
  | 'feliz'
  | 'pensativo'
  | 'surpreso'
  | 'animado'
  | 'atento'
  | 'confiante'
  | 'preocupado'
  | 'triste'
  // Estados do sistema (pose)
  | 'carregando'
  | 'sucesso'
  | 'aviso'
  | 'erro'
  | 'vazio'
  // Genéricos
  | 'avatar'
  | 'hero';

interface Props {
  /** Nome do asset → /kiro/<name>.png */
  name: KiroAsset;
  /**
   * Caminho explícito do asset (sobrepõe `name`). Pra poses novas que vivem em
   * subpastas, ex.: "/kiro/estudio-v4/kiro-caixa-kevins.png". `name` ainda é
   * usado no alt padrão.
   */
  src?: string;
  /** Largura em px (altura é automática, preserva proporção). */
  size?: number;
  alt?: string;
  className?: string;
  /** Exibido se o asset ainda não existe (404). Default: nada. */
  fallback?: React.ReactNode;
}

export function KiroMascot({
  name,
  src,
  size = 120,
  alt,
  className = '',
  fallback = null,
}: Props) {
  const [broken, setBroken] = useState(false);

  if (broken) return <>{fallback}</>;

  return (
    <img
      src={src ?? `/kiro/${name}.png`}
      alt={alt ?? `Kiro — ${name}`}
      className={className}
      style={{ width: size, height: 'auto', objectFit: 'contain' }}
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}
